import React, { useState, useMemo, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { 
  Download, Calendar, ChevronDown, ChevronRight, Filter, Users, X, MessageSquare, Send, Trash2, CheckCircle, Circle
} from 'lucide-react'
import { projectService } from '@/services/projectService'
import { formatCurrency } from '@/utils/formatters'
import * as XLSX from 'xlsx'
import { toast } from 'react-hot-toast'

const ProjectGanttReport = () => {
  // Calculate default date range: 3 months (1 month back, 2 months forward)
  const getDefaultDateRange = () => {
    const today = new Date()
    const start = new Date(today)
    start.setMonth(today.getMonth() - 1) // 1 month back
    start.setDate(1) // First day of that month
    
    const end = new Date(today)
    end.setMonth(today.getMonth() + 2) // 2 months forward
    end.setMonth(end.getMonth() + 1) // Go to next month
    end.setDate(0) // Last day of the previous month (2 months forward)
    
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    }
  }

  const [selectedProject, setSelectedProject] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [showAssignee, setShowAssignee] = useState(true)
  const [expandedMilestones, setExpandedMilestones] = useState({})
  const [expandedBudgets, setExpandedBudgets] = useState({})
  const [dateRange, setDateRange] = useState(getDefaultDateRange())
  const [duration, setDuration] = useState('3')
  const [tooltip, setTooltip] = useState(null)
  const [selectedProjectForComment, setSelectedProjectForComment] = useState(null)
  const [commentText, setCommentText] = useState('')
  const [projectComments, setProjectComments] = useState({})
  const [editingProgress, setEditingProgress] = useState(null) // { type, id, value }
  const [completionNote, setCompletionNote] = useState('')
  const [showNoteModal, setShowNoteModal] = useState(null) // { projectId, milestoneIndex, deliverableIndex }
  const ganttRef = useRef(null)
  const queryClient = useQueryClient()

  // Load comments when project is selected for comment
  useEffect(() => {
    if (selectedProjectForComment) {
      projectService.getProjectComments(selectedProjectForComment)
        .then(res => {
          const comments = res.data?.data?.comments || [];
          setProjectComments(prev => ({
            ...prev,
            [selectedProjectForComment]: comments.map(c => ({
              ...c,
              createdAt: c.created_at || c.timestamp,
              creatorName: c.creator?.first_name ? `${c.creator.first_name} ${c.creator.last_name}` : 'Unknown'
            }))
          }))
        })
        .catch(err => console.error('Failed to load comments:', err))
    }
  }, [selectedProjectForComment])

  // Handle duration preset change
  const handleDurationChange = (value) => {
    setDuration(value)  
    if (value === 'all') {
      setDateRange({ start: '', end: '' })
    } else {
      const today = new Date()
      const start = new Date()
      const months = parseInt(value)
      start.setMonth(today.getMonth() - Math.floor(months / 2))
      const end = new Date()
      end.setMonth(today.getMonth() + Math.ceil(months / 2))
      setDateRange({
        start: start.toISOString().split('T')[0],
        end: end.toISOString().split('T')[0]
      })
    }
  }

  // Handle adding comment
  const handleAddComment = (projectId) => {
    if (!commentText.trim()) return
    
    projectService.addProjectComment(projectId, { text: commentText })
      .then(res => {
        const newComment = res.data?.data;
        setProjectComments(prev => ({
          ...prev,
          [projectId]: [
            ...(prev[projectId] || []),
            {
              ...newComment,
              createdAt: newComment.created_at,
              creatorName: newComment.creator?.first_name ? `${newComment.creator.first_name} ${newComment.creator.last_name}` : 'You'
            }
          ]
        }))
        setCommentText('')
      })
      .catch(err => console.error('Failed to add comment:', err))
  }

  // Handle deleting comment
  const handleDeleteComment = (projectId, commentId) => {
    projectService.deleteProjectComment(projectId, commentId)
      .then(() => {
        setProjectComments(prev => ({
          ...prev,
          [projectId]: prev[projectId].filter(c => c.id !== commentId)
        }))
      })
      .catch(err => console.error('Failed to delete comment:', err))
  }

  // Calculate status based on dates and completion
  const calculateStatus = (item, type = 'milestone') => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    if (item.completed) return 'on_track' // Completed = Green
    
    const dueDate = new Date(type === 'deliverable' ? (item.end_date || item.due_date) : (item.end_date || item.date))
    dueDate.setHours(0, 0, 0, 0)
    
    const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24))
    
    // Critical = Red (overdue or due within 3 days)
    if (daysUntilDue < 0 || daysUntilDue <= 3) return 'critical'
    
    // Delayed = Amber (due within 7 days)
    if (daysUntilDue <= 7) return 'delayed'
    
    // On track = Green (more than 7 days)
    return 'on_track'
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'on_track': return { bg: 'bg-green-500', light: 'bg-green-100', text: 'text-green-700', label: 'On Track' }
      case 'delayed': return { bg: 'bg-amber-500', light: 'bg-amber-100', text: 'text-amber-700', label: 'Delayed' }
      case 'critical': return { bg: 'bg-red-500', light: 'bg-red-100', text: 'text-red-700', label: 'Critical' }
      default: return { bg: 'bg-gray-400', light: 'bg-gray-100', text: 'text-gray-700', label: 'Unknown' }
    }
  }

  // Mutation to update project milestones
  const updateMilestonesMutation = useMutation(
    ({ projectId, milestones }) => {
      // Calculate project progress from all deliverables
      const allDeliverables = milestones.flatMap(m => m.deliverables || [])
      const totalDeliverables = allDeliverables.length
      const completedDeliverables = allDeliverables.filter(d => d.completed).length
      const progress_percentage = totalDeliverables > 0 ? Math.round((completedDeliverables / totalDeliverables) * 100) : 0
      
      return projectService.updateProject(projectId, { milestones, progress_percentage })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('projects-gantt')
        toast.success('Updated successfully')
      },
      onError: () => toast.error('Failed to update')
    }
  )

  // Toggle deliverable completion
  const toggleDeliverableCompletion = (projectId, milestoneIndex, deliverableIndex, currentStatus) => {
    const project = projects.find(p => p.id === projectId)
    if (!project) return

    let milestones = typeof project.milestones === 'string' ? JSON.parse(project.milestones) : project.milestones
    const deliverable = milestones[milestoneIndex].deliverables[deliverableIndex]
    
    if (!currentStatus) {
      setShowNoteModal({ projectId, milestoneIndex, deliverableIndex })
    } else {
      milestones[milestoneIndex].deliverables[deliverableIndex].completed = false
      milestones[milestoneIndex].deliverables[deliverableIndex].completion_note = null
      
      // Recalculate milestone completion
      const totalDeliverables = milestones[milestoneIndex].deliverables.length
      const completedDeliverables = milestones[milestoneIndex].deliverables.filter(d => d.completed).length
      milestones[milestoneIndex].completed = completedDeliverables === totalDeliverables
      milestones[milestoneIndex].progress = Math.round((completedDeliverables / totalDeliverables) * 100)
      
      updateMilestonesMutation.mutate({ projectId, milestones })
    }
  }

  // Save completion with note
  const saveCompletion = () => {
    if (!showNoteModal) return
    
    const { projectId, milestoneIndex, deliverableIndex } = showNoteModal
    const project = projects.find(p => p.id === projectId)
    if (!project) return

    let milestones = typeof project.milestones === 'string' ? JSON.parse(project.milestones) : project.milestones
    
    milestones[milestoneIndex].deliverables[deliverableIndex].completed = true
    milestones[milestoneIndex].deliverables[deliverableIndex].completion_note = completionNote
    milestones[milestoneIndex].deliverables[deliverableIndex].completed_at = new Date().toISOString()
    
    // Recalculate milestone completion
    const totalDeliverables = milestones[milestoneIndex].deliverables.length
    const completedDeliverables = milestones[milestoneIndex].deliverables.filter(d => d.completed).length
    milestones[milestoneIndex].completed = completedDeliverables === totalDeliverables
    milestones[milestoneIndex].progress = Math.round((completedDeliverables / totalDeliverables) * 100)
    
    updateMilestonesMutation.mutate({ projectId, milestones })
    setShowNoteModal(null)
    setCompletionNote('')
  }

  // Fetch projects with milestones
  const { data: projects, isLoading } = useQuery(
    'projects-gantt',
    () => projectService.getProjects({ limit: 100 }),
    {
      select: (response) => response?.data?.data?.projects || []
    }
  )

  // Parse milestones from projects
  const ganttData = useMemo(() => {
    if (!projects) return []
    
    let filtered = projects
    if (selectedProject !== 'all') {
      filtered = filtered.filter(p => p.id === parseInt(selectedProject))
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter)
    }

    return filtered.map(project => {
      let milestones = project.milestones
      console.log('Project:', project.name, 'Raw milestones:', milestones)
      if (typeof milestones === 'string') {
        try { milestones = JSON.parse(milestones) } catch { milestones = [] }
      }
      if (!Array.isArray(milestones)) milestones = []
      console.log('Parsed milestones count:', milestones.length)

      let budgetItems = project.budget_items
      if (typeof budgetItems === 'string') {
        try { budgetItems = JSON.parse(budgetItems) } catch { budgetItems = [] }
      }
      if (!Array.isArray(budgetItems)) budgetItems = []

      // Calculate total budget from items
      const calculatedBudget = budgetItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0)
      const calculatedSpent = budgetItems.reduce((sum, item) => sum + (parseFloat(item.spent) || 0), 0)

      // Calculate project progress from all deliverables
      const allDeliverables = milestones.flatMap(m => m.deliverables || [])
      const totalProjectDeliverables = allDeliverables.length
      const completedProjectDeliverables = allDeliverables.filter(d => d.completed).length
      const projectProgress = totalProjectDeliverables > 0 ? Math.round((completedProjectDeliverables / totalProjectDeliverables) * 100) : 0

      return {
        id: project.id,
        name: project.name,
        code: project.code,
        status: project.status,
        start_date: project.start_date,
        end_date: project.end_date,
        progress: projectProgress,
        budget_allocated: calculatedBudget > 0 ? calculatedBudget : (project.budget_allocated || 0),
        budget_spent: calculatedSpent > 0 ? calculatedSpent : (project.budget_spent || 0),
        manager: project.Manager?.User ? `${project.Manager.User.first_name} ${project.Manager.User.last_name}` : 'Unassigned',
        budget_items: budgetItems.map((item, idx) => ({
          id: `${project.id}-b-${idx}`,
          name: item.name || `Budget Item ${idx + 1}`,
          amount: parseFloat(item.amount) || 0,
          spent: parseFloat(item.spent) || 0,
          category: item.category || 'General',
          description: item.description || ''
        })),
        milestones: milestones.map((m, idx) => {
          // Calculate milestone progress from deliverables
          const totalDeliverables = (m.deliverables || []).length
          const completedDeliverables = (m.deliverables || []).filter(d => d.completed).length
          const calculatedProgress = totalDeliverables > 0 ? Math.round((completedDeliverables / totalDeliverables) * 100) : 0
          const isCompleted = totalDeliverables > 0 && completedDeliverables === totalDeliverables
          
          const milestoneStatus = calculateStatus({ ...m, completed: isCompleted }, 'milestone')
          return {
            id: `${project.id}-m-${idx}`,
            projectId: project.id,
            milestoneIndex: idx,
            name: m.name || `Milestone ${idx + 1}`,
            date: m.date || m.end_date,
            start_date: m.start_date,
            end_date: m.end_date,
            description: m.description,
            completed: isCompleted,
            progress: calculatedProgress,
            status: milestoneStatus,
            deliverables: (m.deliverables || []).map((d, dIdx) => {
              const deliverableStatus = calculateStatus(d, 'deliverable')
              return {
                id: `${project.id}-m-${idx}-d-${dIdx}`,
                projectId: project.id,
                milestoneIndex: idx,
                deliverableIndex: dIdx,
                name: d.name,
                due_date: d.due_date || d.end_date,
                start_date: d.start_date,
                end_date: d.end_date,
                completed: d.completed,
                completion_note: d.completion_note,
                progress: d.completed ? 100 : 0,
                status: deliverableStatus,
                assignee: d.assignee || (project.Manager?.User ? `${project.Manager?.User?.first_name || ''} ${project.Manager?.User?.last_name || ''}`.trim() : 'Unassigned')
              }
            })
          }
        })
      }
    })
  }, [projects, selectedProject, statusFilter])

  // Load comment counts for all visible projects (after ganttData is defined)
  useEffect(() => {
    if (ganttData && ganttData.length > 0) {
      ganttData.forEach(project => {
        if (!projectComments[project.id]) {
          projectService.getProjectComments(project.id)
            .then(res => {
              const comments = res.data?.data?.comments || [];
              setProjectComments(prev => ({
                ...prev,
                [project.id]: comments.map(c => ({
                  ...c,
                  createdAt: c.created_at || c.timestamp,
                  creatorName: c.creator?.first_name ? `${c.creator.first_name} ${c.creator.last_name}` : 'Unknown'
                }))
              }))
            })
            .catch(err => console.error('Failed to load comments:', err))
        }
      })
    }
  }, [ganttData])

  // Calculate timeline range
  const timelineRange = useMemo(() => {
    if (ganttData.length === 0) return { start: new Date(), end: new Date(), days: 30 }
    
    let minDate, maxDate;
    
    // Use selected date range if provided
    if (dateRange.start && dateRange.end) {
      minDate = new Date(dateRange.start);
      maxDate = new Date(dateRange.end);
    } else {
      // Calculate from gantt data
      minDate = new Date();
      maxDate = new Date();
      
      ganttData.forEach(project => {
        const start = new Date(project.start_date);
        const end = new Date(project.end_date);
        if (start < minDate) minDate = start;
        if (end > maxDate) maxDate = end;
        
        project.milestones.forEach(m => {
          if (m.date) {
            const mDate = new Date(m.date);
            if (mDate < minDate) minDate = mDate;
            if (mDate > maxDate) maxDate = mDate;
          }
          m.deliverables.forEach(d => {
            if (d.due_date) {
              const dDate = new Date(d.due_date);
              if (dDate < minDate) minDate = dDate;
              if (dDate > maxDate) maxDate = dDate;
            }
          })
        })
      });

      // Add padding only if not using custom range
      minDate.setDate(minDate.getDate() - 7);
      maxDate.setDate(maxDate.getDate() + 7);
    }
    
    const days = Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24));
    return { start: minDate, end: maxDate, days: Math.max(days, 30) };
  }, [ganttData, dateRange])

  // Generate month headers
  const monthHeaders = useMemo(() => {
    const months = []
    const current = new Date(timelineRange.start)
    current.setDate(1)
    
    while (current <= timelineRange.end) {
      const daysInMonth = new Date(current.getFullYear(), current.getMonth() + 1, 0).getDate()
      const monthStart = new Date(Math.max(current, timelineRange.start))
      const monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0)
      if (monthEnd > timelineRange.end) monthEnd.setTime(timelineRange.end.getTime())
      
      const startDay = Math.floor((monthStart - timelineRange.start) / (1000 * 60 * 60 * 24))
      const endDay = Math.floor((monthEnd - timelineRange.start) / (1000 * 60 * 60 * 24))
      
      months.push({
        label: current.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        startDay,
        width: endDay - startDay + 1
      })
      current.setMonth(current.getMonth() + 1)
    }
    return months
  }, [timelineRange])

  const toggleMilestone = (id) => {
    setExpandedMilestones(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const toggleBudget = (id) => {
    setExpandedBudgets(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const getBarPosition = (startDate, endDate) => {
    const start = new Date(startDate)
    const end = new Date(endDate || startDate)
    const startDay = Math.max(0, Math.floor((start - timelineRange.start) / (1000 * 60 * 60 * 24)))
    const endDay = Math.floor((end - timelineRange.start) / (1000 * 60 * 60 * 24))
    const dayWidth = 100 / timelineRange.days
    return {
      left: `${startDay * dayWidth}%`,
      width: `${Math.max((endDay - startDay + 1) * dayWidth, 1)}%`
    }
  }

  const showTooltip = (e, item, type) => {
    const rect = e.currentTarget.getBoundingClientRect()
    setTooltip({
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
      item,
      type
    })
  }

  const exportToExcel = () => {
    // Only export filtered data (respects project selection)
    const dataToExport = ganttData // This already respects the selectedProject filter
    
    if (dataToExport.length === 0) {
      alert('No data to export. Please select a project or adjust your filters.')
      return
    }
    
    const rows = []
    dataToExport.forEach(project => {
      // Project row
      rows.push({
        'Type': 'Project',
        'Project': project.name,
        'Code': project.code,
        'Item': project.name,
        'Start Date': project.start_date,
        'End Date': project.end_date,
        'Due Date': project.end_date,
        'Status': project.status,
        'Progress': `${project.progress}%`,
        'Budget Allocated': project.budget_allocated,
        'Budget Spent': project.budget_spent,
        'Manager/Assignee': project.manager
      })
      
      // Budget Items
      project.budget_items.forEach(item => {
        const utilization = item.amount > 0 ? (item.spent / item.amount) * 100 : 0
        rows.push({
          'Type': 'Budget Item',
          'Project': project.name,
          'Code': project.code,
          'Item': item.name,
          'Category': item.category || '',
          'Description': item.description || '',
          'Amount Allocated': item.amount,
          'Amount Spent': item.spent,
          'Remaining': item.amount - item.spent,
          'Utilization %': utilization.toFixed(2)
        })
      })
      
      // Milestones
      project.milestones.forEach(m => {
        const statusColor = getStatusColor(m.status)
        rows.push({
          'Type': 'Milestone',
          'Project': project.name,
          'Code': project.code,
          'Item': m.name,
          'Start Date': m.date || '',
          'End Date': m.date || '',
          'Due Date': m.date || '',
          'Status': statusColor.label,
          'Progress': `${m.progress}%`,
          'Manager/Assignee': ''
        })
        // Deliverables
        m.deliverables.forEach(d => {
          const delStatusColor = getStatusColor(d.status)
          rows.push({
            'Type': 'Deliverable',
            'Project': project.name,
            'Code': project.code,
            'Item': d.name,
            'Start Date': d.due_date || '',
            'End Date': d.due_date || '',
            'Due Date': d.due_date || '',
            'Status': delStatusColor.label,
            'Progress': `${d.progress}%`,
            'Manager/Assignee': d.assignee || ''
          })
        })
      })
    })
    
    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Gantt Chart')
    
    // Generate filename with project name if single project selected
    const filename = selectedProject !== 'all' 
      ? `gantt_${dataToExport[0]?.code || 'project'}_${new Date().toISOString().split('T')[0]}.xlsx`
      : `gantt_chart_${new Date().toISOString().split('T')[0]}.xlsx`
    
    XLSX.writeFile(wb, filename)
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Project Gantt Chart</h1>
          <p className="text-gray-600">Visual timeline of project milestones and deliverables</p>
        </div>
        <button onClick={exportToExcel} className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
          <Download className="h-4 w-4 mr-2" /> Export Excel
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="all">All Projects</option>
              {projects?.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="all">All Status</option>
              <option value="planning">Planning</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="on_hold">On Hold</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
            <select
              value={duration}
              onChange={(e) => handleDurationChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="all">All Time</option>
              <option value="1">1 Month</option>
              <option value="3">3 Months</option>
              <option value="6">6 Months</option>
              <option value="12">12 Months</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => { setDateRange(prev => ({ ...prev, start: e.target.value })); setDuration('custom'); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => { setDateRange(prev => ({ ...prev, end: e.target.value })); setDuration('custom'); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showAssignee}
                onChange={(e) => setShowAssignee(e.target.checked)}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Show Assignee</span>
            </label>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-500 rounded"></div>
          <span>On Track (Green)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-amber-500 rounded"></div>
          <span>Delayed (Amber)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-500 rounded"></div>
          <span>Critical (Red)</span>
        </div>
        <div className="text-gray-600 ml-4">
          • Critical: Overdue or due within 3 days • Delayed: Due within 7 days • On Track: More than 7 days
        </div>
      </div>

      {/* Gantt Chart */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto" ref={ganttRef}>
          <div className="min-w-[1200px]">
            {/* Timeline Header */}
            <div className="flex border-b bg-gray-50 sticky top-0 z-10">
              <div className="w-80 flex-shrink-0 px-4 py-3 font-semibold text-gray-700 border-r">
                Milestone / Deliverable / Budget Item
              </div>
              {showAssignee && (
                <div className="w-32 flex-shrink-0 px-3 py-3 font-semibold text-gray-700 border-r text-sm">
                  Assigned To
                </div>
              )}
              <div className="w-28 flex-shrink-0 px-3 py-3 font-semibold text-gray-700 border-r text-sm text-center">
                Due Date
              </div>
              <div className="w-24 flex-shrink-0 px-3 py-3 font-semibold text-gray-700 border-r text-sm text-center">
                Status
              </div>
              <div className="w-24 flex-shrink-0 px-3 py-3 font-semibold text-gray-700 border-r text-sm text-center">
                Progress
              </div>
              <div className="w-32 flex-shrink-0 px-3 py-3 font-semibold text-gray-700 border-r text-sm text-center">
                Budget
              </div>
              <div className="flex-1 relative">
                <div className="flex">
                  {monthHeaders.map((month, idx) => (
                    <div
                      key={idx}
                      className="text-center py-3 text-sm font-medium text-gray-600 border-r"
                      style={{ width: `${(month.width / timelineRange.days) * 100}%` }}
                    >
                      {month.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Gantt Rows */}
            <div className="divide-y">
              {ganttData.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No projects found</div>
              ) : (
                ganttData.map(project => (
                  <div key={project.id}>
                    {/* Project Header */}
                    <div className="flex bg-gray-100 border-b">
                      <div className="w-80 flex-shrink-0 px-4 py-3 font-semibold text-gray-900 border-r">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className="text-primary-600">{project.code}</span>
                            <span>-</span>
                            <span className="truncate">{project.name}</span>
                          </div>
                          <button
                            onClick={() => setSelectedProjectForComment(project.id)}
                            className="relative p-2 hover:bg-primary-100 rounded-lg flex-shrink-0 transition-colors group"
                            title="View/Add comments"
                          >
                            <MessageSquare className="h-5 w-5 text-primary-600 group-hover:text-primary-700" />
                            {projectComments[project.id]?.length > 0 && (
                              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-semibold">
                                {projectComments[project.id].length}
                              </span>
                            )}
                          </button>
                        </div>
                      </div>
                      {showAssignee && (
                        <div className="w-32 flex-shrink-0 px-3 py-3 text-sm text-gray-600 border-r">
                          {project.manager}
                        </div>
                      )}
                      <div className="w-28 flex-shrink-0 px-3 py-3 text-sm text-gray-600 border-r text-center">
                        {new Date(project.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                      <div className="w-24 flex-shrink-0 px-3 py-3 border-r">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          project.status === 'completed' ? 'bg-green-100 text-green-700' :
                          project.status === 'active' ? 'bg-blue-100 text-blue-700' :
                          project.status === 'on_hold' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {project.status}
                        </span>
                      </div>
                      <div className="w-24 flex-shrink-0 px-3 py-3 border-r text-center text-sm font-medium">
                        {project.progress}%
                      </div>
                      <div className="w-32 flex-shrink-0 px-3 py-3 text-sm text-gray-600 border-r text-center">
                        <div className="text-xs">
                          <div className="font-medium text-gray-900">₦{project.budget_allocated.toLocaleString()}</div>
                          <div className="text-gray-500">Spent: ₦{project.budget_spent.toLocaleString()}</div>
                        </div>
                      </div>
                      <div className="flex-1 relative py-3 px-2">
                        {/* Project bar */}
                        <div
                          className="absolute h-6 bg-primary-200 rounded cursor-pointer hover:bg-primary-300"
                          style={getBarPosition(project.start_date, project.end_date)}
                          onMouseEnter={(e) => showTooltip(e, project, 'project')}
                          onMouseLeave={() => setTooltip(null)}
                        >
                          <div
                            className="h-full bg-primary-500 rounded-l"
                            style={{ width: `${project.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    {/* Budget Section */}
                    {project.budget_items.length > 0 && (
                      <>
                        {/* Budget Header Row */}
                        <div className="flex hover:bg-gray-50 bg-blue-50/30">
                          <div className="w-80 flex-shrink-0 px-4 py-2 border-r">
                            <div className="flex items-center gap-2 pl-4">
                              <button
                                onClick={() => toggleBudget(project.id)}
                                className="p-0.5 hover:bg-gray-200 rounded"
                              >
                                {expandedBudgets[project.id] ? (
                                  <ChevronDown className="h-4 w-4 text-gray-500" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-gray-500" />
                                )}
                              </button>
                              <span className="text-lg">💰</span>
                              <span className="font-medium text-gray-800">Budget</span>
                              <span className="text-xs text-gray-500">({project.budget_items.length} items)</span>
                            </div>
                          </div>
                          {showAssignee && (
                            <div className="w-32 flex-shrink-0 px-3 py-2 text-sm text-gray-500 border-r">-</div>
                          )}
                          <div className="w-28 flex-shrink-0 px-3 py-2 text-sm text-gray-600 border-r text-center">-</div>
                          <div className="w-24 flex-shrink-0 px-3 py-2 border-r">
                            <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                              Budget
                            </span>
                          </div>
                          <div className="w-24 flex-shrink-0 px-3 py-2 border-r text-center text-sm">-</div>
                          <div className="w-32 flex-shrink-0 px-3 py-2 text-sm border-r text-center">
                            <div className="text-xs">
                              <div className="font-semibold text-gray-900">₦{project.budget_allocated.toLocaleString()}</div>
                              <div className="text-gray-500">₦{project.budget_spent.toLocaleString()}</div>
                            </div>
                          </div>
                          <div className="flex-1 relative py-2 px-2">
                            {/* Budget progress bar */}
                            <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-green-500 to-green-600 transition-all"
                                style={{ width: `${Math.min(100, (project.budget_spent / project.budget_allocated) * 100)}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>

                        {/* Budget Items */}
                        {expandedBudgets[project.id] && project.budget_items.map(item => {
                          const utilization = item.amount > 0 ? (item.spent / item.amount) * 100 : 0
                          const utilizationColor = utilization > 90 ? 'text-red-600' : utilization > 70 ? 'text-amber-600' : 'text-green-600'
                          
                          return (
                            <div key={item.id} className="flex hover:bg-blue-50 bg-gray-50/50">
                              <div className="w-80 flex-shrink-0 px-4 py-2 border-r">
                                <div className="flex items-center gap-2 pl-12">
                                  <div className={`w-1.5 h-1.5 rounded-full ${utilization > 90 ? 'bg-red-500' : utilization > 70 ? 'bg-amber-500' : 'bg-green-500'}`}></div>
                                  <span className="text-sm text-gray-700 font-medium">{item.name}</span>
                                  {item.category && (
                                    <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
                                      {item.category}
                                    </span>
                                  )}
                                </div>
                                {item.description && (
                                  <div className="text-xs text-gray-500 pl-14 mt-1">{item.description}</div>
                                )}
                              </div>
                              {showAssignee && (
                                <div className="w-32 flex-shrink-0 px-3 py-2 text-xs text-gray-500 border-r">-</div>
                              )}
                              <div className="w-28 flex-shrink-0 px-3 py-2 text-sm text-gray-600 border-r text-center">-</div>
                              <div className="w-24 flex-shrink-0 px-3 py-2 border-r">
                                <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                                  utilization > 90 ? 'bg-red-100 text-red-700' : 
                                  utilization > 70 ? 'bg-amber-100 text-amber-700' : 
                                  'bg-green-100 text-green-700'
                                }`}>
                                  {utilization.toFixed(0)}%
                                </span>
                              </div>
                              <div className="w-24 flex-shrink-0 px-3 py-2 border-r text-center">
                                <span className={`text-xs font-semibold ${utilizationColor}`}>
                                  {utilization.toFixed(0)}%
                                </span>
                              </div>
                              <div className="w-32 flex-shrink-0 px-3 py-2 border-r text-center">
                                <div className="text-xs space-y-0.5">
                                  <div className="font-medium text-gray-900">₦{item.amount.toLocaleString()}</div>
                                  <div className="text-gray-500">₦{item.spent.toLocaleString()}</div>
                                  <div className="text-gray-400 text-[10px]">
                                    Remaining: ₦{(item.amount - item.spent).toLocaleString()}
                                  </div>
                                </div>
                              </div>
                              <div className="flex-1 relative py-2 px-2">
                                {/* Budget item progress bar */}
                                <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full transition-all ${
                                      utilization > 90 ? 'bg-gradient-to-r from-red-500 to-red-600' : 
                                      utilization > 70 ? 'bg-gradient-to-r from-amber-500 to-amber-600' : 
                                      'bg-gradient-to-r from-green-500 to-green-600'
                                    }`}
                                    style={{ width: `${Math.min(100, utilization)}%` }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </>
                    )}

                    {/* Milestones */}
                    {project.milestones.map(milestone => {
                      const isExpanded = expandedMilestones[milestone.id]
                      const statusColor = getStatusColor(milestone.status)
                      const completedDel = milestone.deliverables.filter(d => d.completed).length
                      const progress = milestone.deliverables.length > 0 
                        ? Math.round((completedDel / milestone.deliverables.length) * 100) 
                        : (milestone.completed ? 100 : 0)

                      return (
                        <div key={milestone.id}>
                          {/* Milestone Row */}
                          <div className="flex hover:bg-gray-50">
                            <div className="w-80 flex-shrink-0 px-4 py-2 border-r">
                              <div className="flex items-center gap-2 pl-4">
                                <button
                                  onClick={() => toggleMilestone(milestone.id)}
                                  className="p-0.5 hover:bg-gray-200 rounded"
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="h-4 w-4 text-gray-500" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 text-gray-500" />
                                  )}
                                </button>
                                <div className={`w-2 h-2 rounded-full ${statusColor.bg}`}></div>
                                <span className="font-medium text-gray-800">{milestone.name}</span>
                                <span className="text-xs text-gray-500">({milestone.deliverables.length})</span>
                              </div>
                            </div>
                            {showAssignee && (
                              <div className="w-32 flex-shrink-0 px-3 py-2 text-sm text-gray-500 border-r">-</div>
                            )}
                            <div className="w-28 flex-shrink-0 px-3 py-2 text-sm text-gray-600 border-r text-center">
                              {milestone.date ? new Date(milestone.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}
                            </div>
                            <div className="w-24 flex-shrink-0 px-3 py-2 border-r">
                              <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${statusColor.light} ${statusColor.text}`}>
                                {statusColor.label}
                              </span>
                            </div>
                            <div className="w-24 flex-shrink-0 px-3 py-2 border-r text-center text-sm">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={milestone.progress}
                                onChange={(e) => {
                                  const newProgress = Math.min(100, Math.max(0, parseInt(e.target.value) || 0))
                                  // Update milestone progress in ganttData
                                  // This is a simplified version - you'd want to persist this to backend
                                  console.log(`Update milestone ${milestone.id} progress to ${newProgress}%`)
                                }}
                                className="w-16 px-2 py-1 text-center border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                                title="Click to edit progress"
                              />
                              <span className="text-xs">%</span>
                            </div>
                            <div className="flex-1 relative py-2 px-2">
                              {/* Milestone marker */}
                              {milestone.date && (
                                <div
                                  className={`absolute w-4 h-4 ${statusColor.bg} transform rotate-45 cursor-pointer`}
                                  style={{
                                    left: getBarPosition(milestone.date, milestone.date).left,
                                    top: '50%',
                                    marginTop: '-8px'
                                  }}
                                  onMouseEnter={(e) => showTooltip(e, milestone, 'milestone')}
                                  onMouseLeave={() => setTooltip(null)}
                                ></div>
                              )}
                            </div>
                          </div>

                          {/* Deliverables */}
                          {isExpanded && milestone.deliverables.map(deliverable => {
                            const delStatus = getStatusColor(deliverable.status)
                            return (
                              <div key={deliverable.id} className="flex hover:bg-blue-50 bg-gray-50/50">
                                <div className="w-80 flex-shrink-0 px-4 py-2 border-r">
                                  <div className="flex items-center gap-2 pl-12">
                                    <button
                                      onClick={() => toggleDeliverableCompletion(
                                        deliverable.projectId,
                                        deliverable.milestoneIndex,
                                        deliverable.deliverableIndex,
                                        deliverable.completed
                                      )}
                                      className="flex-shrink-0"
                                      title={deliverable.completed ? `Completed${deliverable.completion_note ? `: ${deliverable.completion_note}` : ''}` : 'Mark as complete'}
                                    >
                                      {deliverable.completed ? (
                                        <CheckCircle className="h-4 w-4 text-green-600" />
                                      ) : (
                                        <Circle className="h-4 w-4 text-gray-400 hover:text-green-600" />
                                      )}
                                    </button>
                                    <div className={`w-1.5 h-1.5 rounded-full ${delStatus.bg}`}></div>
                                    <span className="text-sm text-gray-700">{deliverable.name}</span>
                                  </div>
                                </div>
                                {showAssignee && (
                                  <div className="w-32 flex-shrink-0 px-3 py-2 text-xs text-gray-500 border-r truncate">
                                    {deliverable.assignee}
                                  </div>
                                )}
                                <div className="w-28 flex-shrink-0 px-3 py-2 text-sm text-gray-600 border-r text-center">
                                  {deliverable.due_date ? new Date(deliverable.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}
                                </div>
                                <div className="w-24 flex-shrink-0 px-3 py-2 border-r">
                                  <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${delStatus.light} ${delStatus.text}`}>
                                    {delStatus.label}
                                  </span>
                                </div>
                                <div className="w-24 flex-shrink-0 px-3 py-2 border-r text-center text-sm font-medium">
                                  {deliverable.progress}%
                                </div>
                                <div className="w-32 flex-shrink-0 px-3 py-2 border-r text-center text-sm">-</div>
                                <div className="flex-1 relative py-2 px-2">
                                  {/* Deliverable bar */}
                                  {deliverable.due_date && (
                                    <div
                                      className={`absolute h-4 ${delStatus.bg} rounded cursor-pointer opacity-80 hover:opacity-100`}
                                      style={{
                                        ...getBarPosition(deliverable.due_date, deliverable.due_date),
                                        minWidth: '8px'
                                      }}
                                      onMouseEnter={(e) => showTooltip(e, deliverable, 'deliverable')}
                                      onMouseLeave={() => setTooltip(null)}
                                    ></div>
                                  )}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )
                    })}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="fixed z-50 bg-gray-900 text-white text-sm rounded-lg shadow-lg p-3 pointer-events-none"
          style={{
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translate(-50%, -100%)'
          }}
        >
          <div className="font-semibold mb-1">{tooltip.item.name}</div>
          {tooltip.type === 'project' && (
            <>
              <div className="text-gray-300">Manager: {tooltip.item.manager}</div>
              <div className="text-gray-300">
                {new Date(tooltip.item.start_date).toLocaleDateString()} - {new Date(tooltip.item.end_date).toLocaleDateString()}
              </div>
              <div className="text-gray-300">Progress: {tooltip.item.progress}%</div>
            </>
          )}
          {tooltip.type === 'milestone' && (
            <>
              <div className="text-gray-300">Target: {new Date(tooltip.item.date).toLocaleDateString()}</div>
              <div className="text-gray-300">Status: {tooltip.item.status}</div>
              {tooltip.item.description && <div className="text-gray-300 mt-1">{tooltip.item.description}</div>}
            </>
          )}
          {tooltip.type === 'deliverable' && (
            <>
              <div className="text-gray-300">Due: {new Date(tooltip.item.due_date).toLocaleDateString()}</div>
              <div className="text-gray-300">Assigned: {tooltip.item.assignee}</div>
              <div className="text-gray-300">Status: {tooltip.item.status}</div>
            </>
          )}
        </div>
      )}

      {/* Comment Modal */}
      {selectedProjectForComment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-primary-50 to-blue-50">
              <div>
                <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <MessageSquare className="h-6 w-6 text-primary-600" />
                  Project Comments
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {ganttData.find(p => p.id === selectedProjectForComment)?.name}
                </p>
              </div>
              <button
                onClick={() => {
                  setSelectedProjectForComment(null)
                  setCommentText('')
                }}
                className="p-2 hover:bg-white rounded-lg transition-colors"
                title="Close"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Comments List */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50">
              {projectComments[selectedProjectForComment]?.length > 0 ? (
                projectComments[selectedProjectForComment].map((comment, index) => (
                  <div 
                    key={comment.id} 
                    className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        {/* Comment Header */}
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-sm font-semibold text-primary-700">
                              {comment.creatorName?.charAt(0)?.toUpperCase() || 'U'}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900">
                              {comment.creatorName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(comment.createdAt).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                        
                        {/* Comment Text */}
                        <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                          {comment.text}
                        </p>
                      </div>
                      
                      {/* Delete Button */}
                      <button
                        onClick={() => handleDeleteComment(selectedProjectForComment, comment.id)}
                        className="p-2 hover:bg-red-50 rounded-lg text-red-600 flex-shrink-0 transition-colors"
                        title="Delete comment"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg font-medium">No comments yet</p>
                  <p className="text-gray-400 text-sm mt-1">Be the first to add a comment!</p>
                </div>
              )}
            </div>

            {/* Add Comment Section */}
            <div className="p-6 border-t bg-white space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Add a comment
              </label>
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Share your thoughts, updates, or questions about this project..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                rows={4}
              />
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  {commentText.length} characters
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCommentText('')}
                    className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    disabled={!commentText.trim()}
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => {
                      if (commentText.trim()) {
                        handleAddComment(selectedProjectForComment)
                      }
                    }}
                    disabled={!commentText.trim()}
                    className="inline-flex items-center px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Post Comment
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Completion Note Modal */}
      {showNoteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Mark as Complete</h3>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Completion Note (Optional)
            </label>
            <textarea
              value={completionNote}
              onChange={(e) => setCompletionNote(e.target.value)}
              placeholder="Add any notes about this completion..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              rows={4}
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setShowNoteModal(null)
                  setCompletionNote('')
                }}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={saveCompletion}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
              >
                Mark Complete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProjectGanttReport
